import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Card } from 'primeng/card';
import { Message } from 'primeng/message';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonDirective } from 'primeng/button';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { SsoService } from '../../../services/sso.service';
import { AuthService } from '../../../services/auth.service';
import { CustomerService } from '../../../services/customer.service';
import { LocaleService } from '../../../services/locale.service';
import { PostLoginNavigationService } from '../../../services/post-login-navigation.service';
import { UtilsService } from '../../../services/utils.service';

/**
 * Entra ID (OIDC) login entry point, one per customer Organization
 * (/login/:orgSlug to start, /login/:orgSlug/callback to complete) -
 * mirrors EraLoginComponent's redirect-out/confirm-back shape, but unlike
 * eRa this establishes the *first* Aline session rather than extending an
 * existing one.
 */
@Component({
  selector: 'app-sso-login',
  templateUrl: './sso-login.component.html',
  styleUrls: ['./sso-login.component.scss'],

  imports: [
    Card,
    Message,
    TranslatePipe,
    RouterLink,
    ButtonDirective,
    Select,
    FormsModule
  ]
})
export class SsoLoginComponent implements OnInit {
  public loginEvent = '';
  public errorMessage: string | undefined = undefined;
  public retry = false;
  public organizationUnits: Array<any> = [];
  public selectedOrganizationUnit: string | undefined = undefined;
  public selectActiveOrganizationUnit = false;

  private orgSlug = '';

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ssoService: SsoService,
    private authService: AuthService,
    private customerService: CustomerService,
    private localeService: LocaleService,
    private postLoginNavigationService: PostLoginNavigationService,
    private translateService: TranslateService,
    private utilsService: UtilsService
  ) {}

  ngOnInit(): void {
    this.orgSlug = this.activatedRoute.snapshot.paramMap.get('orgSlug') || '';
    this.activatedRoute.data.subscribe((result) => {
      this.loginEvent = result.loginEvent;
      switch (this.loginEvent) {
        case 'start':
          this.startLogin();
          break;
        case 'confirm':
          this.confirmLogin();
          break;
      }
    });
  }

  /**
   * Fetches the Entra ID authorization URL and redirects the browser to it.
   */
  startLogin(): void {
    this.ssoService.ssoLogin(this.orgSlug).subscribe(
      (result) => {
        if (result?.login_url) {
          window.location.href = result.login_url;
        }
      },
      (error) => {
        this.errorMessage = error.message;
        this.retry = true;
      }
    );
  }

  /**
   * Exchanges the one-time code from the /oidc/callback redirect for a
   * session, and applies it - same post-login navigation as password
   * login (LoginComponent.doLogin).
   */
  async confirmLogin(): Promise<void> {
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    const oidcError = queryParams.get('error');
    if (oidcError) {
      this.errorMessage = this.translateService.instant(
        `sso-login.error.${oidcError}`
      );
      this.retry = true;
      return;
    }

    const exchangeCode = queryParams.get('xc');
    if (!exchangeCode) {
      this.errorMessage = this.translateService.instant(
        'sso-login.error.OIDC_LOGIN_FAILED'
      );
      this.retry = true;
      return;
    }

    this.ssoService.setSsoConfirmInProgress(true);
    try {
      await this.ssoService.ssoConfirm(exchangeCode);
      // A new login must never keep the previous session's patient in the
      // top bar, whether that session ended cleanly or not.
      this.customerService.setSelectedCustomer(null);
      const profile = this.authService.getUserProfile();
      // The user's language must have loaded before the menus are built in it.
      await this.localeService.setLanguage(profile!.language);

      if (profile?.organizationUnits && profile.organizationUnits.length > 1) {
        this.organizationUnits = profile.organizationUnits;
        this.selectActiveOrganizationUnit = true;
      } else {
        this.postLoginNavigationService.redirectToApplication();
      }
    } catch (error: any) {
      this.utilsService.showError(error.message);
      this.errorMessage = error.message;
      this.retry = true;
    } finally {
      this.ssoService.setSsoConfirmInProgress(false);
    }
  }

  selectOrganization(): void {
    this.customerService.setSelectedCustomer(null);
    const organizationUnit = this.organizationUnits.find(
      (item) => item.id === this.selectedOrganizationUnit
    );
    this.authService.changeActiveOrganizationUnit(organizationUnit).subscribe(
      () => {
        this.postLoginNavigationService.redirectToApplication();
      },
      (error) => {
        this.utilsService.showError(error.message);
      }
    );
  }
}
